import { useCallback, useRef } from 'react';

import { useStartPagePdfExport } from './useStartPagePdfExport.ts';

export type PagePdfExportPrintShortcutHandler = (pageId: string) => Promise<boolean>;

/**
 * Adapter for the Cmd/Ctrl+P print shortcut. Delegates to the shared page PDF export flow
 * (`useStartPagePdfExport`) so the shortcut and the overflow-menu item share one create → route →
 * modal implementation, including the real "Try again".
 *
 * Returns whether the shortcut handled the export: `true` when the modal was opened (V3 / error),
 * `false` for a legacy (V1/V2) decision so the caller can run its own hidden-action fallback (which
 * carries the print banner param). `url` is intentionally not passed to `startExport` — the caller
 * (`usePrintIntercept`) owns the legacy redirect — so a legacy decision returns here without
 * navigating.
 */
export const usePagePdfExportPrintShortcut = (): PagePdfExportPrintShortcutHandler => {
	const { startExport } = useStartPagePdfExport();
	const isCreatingRef = useRef(false);

	return useCallback(
		async (pageId: string): Promise<boolean> => {
			if (isCreatingRef.current) {
				return true;
			}

			isCreatingRef.current = true;

			try {
				// No contentType: the print route only exposes the page id, so the ARI defaults to a page
				// ARI (the backend resolves the content by id regardless of the ARI's content type).
				const decision = await startExport({ contentId: pageId, source: 'print-shortcut' });
				return decision.type !== 'legacy';
			} finally {
				isCreatingRef.current = false;
			}
		},
		[startExport],
	);
};
